import { Link, useNavigate, useParams } from "react-router";
import {
  ArrowLeft,
  ArrowRight,
  Boxes,
  Check,
  FileText,
  MapPin,
  MessageCircleQuestion,
  User,
  X,
} from "lucide-react";
import {
  Body,
  Caption,
  Card,
  CardTitle,
  EmptyState,
  ErrorMessage,
  LoadingState,
  PageHeader,
  SectionTitle,
  VBadge,
  VButton,
} from "@/components/ui";
import { AssetTypeIcon, useAssetTypeLabel } from "@/domains/assets";
import type {
  RequestDecision,
  RequestedAssetLine,
} from "@/domains/requests/api/types";
import { useRequest } from "@/domains/requests/api/queries";
import { RequestStatusBadge } from "@/domains/requests/components/requestStatusBadge";
import { RequestDecisionActions } from "@/domains/requests/components/requestDecisionActions";

const POLE_LABELS = {
  yes: "Ja",
  no: "Neen",
  needsToBePlaced: "Moet er een paal komen",
  notApplicable: "Niet van toepassing",
} as const;

export function RequestDetailPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isPending, isError } = useRequest(id ?? "");

  if (isPending) return <LoadingState label="Aanvraag laden…" />;
  if (isError) return <ErrorMessage message="Kon aanvraag niet ophalen." />;
  if (!data)
    return (
      <EmptyState
        title="Aanvraag niet gevonden"
        action={
          <VButton variant="outline" onClick={() => navigate("/requests")}>
            Terug naar lijst
          </VButton>
        }
      />
    );

  const caseNumber = data.caseId ? data.caseId.replace("case-", "") : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link
          to="/requests"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" aria-hidden />
          Terug naar aanvragen
        </Link>
        {data.caseId && caseNumber ? (
          <Link
            to={`/cases/${data.caseId}`}
            className="group inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:text-primary-800"
          >
            <FileText className="size-4" aria-hidden />
            <span>
              Bijhorend dossier{" "}
              <span className="font-semibold">#{caseNumber}</span> bekijken
            </span>
            <ArrowRight
              className="size-4 transition-transform group-hover:translate-x-0.5"
              aria-hidden
            />
          </Link>
        ) : null}
      </div>

      <PageHeader
        title={data.title}
        subtitle={`Aanvraag ${data.number} — ingediend op ${data.submittedAt.slice(0, 10)}`}
        badge={<RequestStatusBadge status={data.status} />}
        actions={<RequestDecisionActions request={data} />}
      />

      <DecisionTimeline decisions={data.decisions ?? []} />

      <div className="grid gap-4 lg:grid-cols-3">
        <Card padding="lg" className="space-y-3">
          <div className="flex items-center gap-2">
            <User className="size-4 text-primary-700" aria-hidden />
            <CardTitle>Aanvrager</CardTitle>
          </div>
          <Body weight="medium">
            {data.requester.firstName} {data.requester.lastName}
          </Body>
          <Caption tone="muted">{data.requester.service}</Caption>
          <div className="space-y-1 text-sm">
            <div>{data.requester.email}</div>
            <div>{data.requester.phone}</div>
          </div>
        </Card>

        <Card padding="lg" className="space-y-3">
          <CardTitle>Periode</CardTitle>
          <div className="space-y-1 text-sm">
            <div>
              <Caption tone="muted">Begin</Caption>
              <Body weight="medium">{data.period.startDate}</Body>
            </div>
            <div>
              <Caption tone="muted">Eind</Caption>
              <Body weight="medium">{data.period.endDate}</Body>
            </div>
            <div>
              <Caption tone="muted">Verlenging mogelijk</Caption>
              <Body>{data.period.extensionPossible ? "Ja" : "Neen"}</Body>
            </div>
          </div>
        </Card>

        <Card padding="lg" className="space-y-3">
          <div className="flex items-center gap-2">
            <MapPin className="size-4 text-primary-700" aria-hidden />
            <CardTitle>Locatie</CardTitle>
          </div>
          <Body weight="medium">{data.location.address}</Body>
          <Caption tone="muted">
            Paal aanwezig: {POLE_LABELS[data.location.polePresence]}
          </Caption>
          <p className="text-sm text-foreground">{data.location.locationAndCameraDirection}</p>
          {data.location.environmentalFactors ? (
            <div className="space-y-1 rounded-md bg-muted/50 px-3 py-2">
              <Caption as="p" tone="muted">
                Omgevingsfactoren
              </Caption>
              <Body>{data.location.environmentalFactors}</Body>
            </div>
          ) : null}
        </Card>
      </div>

      {data.assets && data.assets.length > 0 ? (
        <Card padding="lg" className="space-y-3">
          <div className="flex items-center gap-2">
            <Boxes className="size-4 text-primary-700" aria-hidden />
            <CardTitle>Aangevraagd materiaal</CardTitle>
          </div>
          <ul className="divide-y divide-border rounded-md border border-border bg-muted/20">
            {data.assets.map((line) => (
              <AssetLineItem key={line.id} line={line} />
            ))}
          </ul>
        </Card>
      ) : null}

      {data.location.photos.length > 0 ? (
        <section className="space-y-3">
          <SectionTitle>Foto's plaatsbezoek</SectionTitle>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
            {data.location.photos.map((p) => (
              <Card key={p.id} padding="none" className="overflow-hidden">
                <img
                  src={p.url}
                  alt={p.cameraDirection ?? p.filename}
                  className="aspect-video w-full object-cover"
                />
                <div className="px-3 py-2">
                  <Body weight="medium" className="truncate">
                    {p.filename}
                  </Body>
                  {p.cameraDirection ? (
                    <Caption tone="muted">{p.cameraDirection}</Caption>
                  ) : null}
                </div>
              </Card>
            ))}
          </div>
        </section>
      ) : null}

      <section className="grid gap-4 lg:grid-cols-2">
        <Card padding="lg" className="space-y-2">
          <CardTitle>Motivatie</CardTitle>
          <Body>{data.motivation.reason}</Body>
          {data.motivation.pvNumber ? (
            <div>
              <Caption tone="muted">PV-nummer</Caption>
              <Body weight="medium">{data.motivation.pvNumber}</Body>
            </div>
          ) : null}
          {data.motivation.specialAuthorization ? (
            <div>
              <Caption tone="muted">Bijzondere machtiging</Caption>
              <Body>{data.motivation.specialAuthorization}</Body>
            </div>
          ) : null}
          {data.motivation.multipleDeployment ? (
            <div>
              <Caption tone="muted">Meervoudige inzet</Caption>
              <Body>{data.motivation.multipleDeployment}</Body>
            </div>
          ) : null}
        </Card>

        <Card padding="lg" className="space-y-2">
          <CardTitle>Evenement & praktisch</CardTitle>
          <div>
            <Caption tone="muted">Evenement</Caption>
            <Body>{data.extraInfo.event || "Geen"}</Body>
          </div>
          {data.extraInfo.eventInfo ? (
            <div>
              <Caption tone="muted">Info evenement</Caption>
              <Body>{data.extraInfo.eventInfo}</Body>
            </div>
          ) : null}
          <div>
            <Caption tone="muted">Parkeerborden</Caption>
            <Body>{data.extraInfo.parkingSigns || "Geen plaatsing vereist."}</Body>
          </div>
        </Card>
      </section>
    </div>
  );
}

function AssetLineItem({ line }: { line: RequestedAssetLine }): React.JSX.Element {
  const label = useAssetTypeLabel(line.assetType);
  return (
    <li className="flex items-start gap-3 px-3 py-2">
      <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-primary-50">
        <AssetTypeIcon
          type={line.assetType}
          className="size-4 text-primary-700"
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <Body weight="medium">{label || "Type niet ingevuld"}</Body>
          <Caption tone="muted">× {line.quantity}</Caption>
        </div>
        {line.note ? <Caption tone="muted">{line.note}</Caption> : null}
      </div>
    </li>
  );
}

type DecisionTimelineProps = {
  decisions: ReadonlyArray<RequestDecision>;
};

type IconType = React.ComponentType<{ className?: string }>;

const DECISION_META: Record<
  RequestDecision["kind"],
  {
    label: string;
    color: React.ComponentProps<typeof VBadge>["color"];
    icon: IconType;
  }
> = {
  approved: { label: "Goedgekeurd", color: "success", icon: Check },
  rejected: { label: "Afgewezen", color: "error", icon: X },
  clarificationRequested: {
    label: "Verduidelijking gevraagd",
    color: "warning",
    icon: MessageCircleQuestion,
  },
};

function DecisionTimeline({ decisions }: DecisionTimelineProps): React.JSX.Element | null {
  if (decisions.length === 0) return null;
  const ordered = [...decisions].sort((a, b) => (a.at < b.at ? 1 : -1));
  return (
    <Card padding="lg" className="space-y-3">
      <CardTitle>Beslissingen</CardTitle>
      <ul className="space-y-2">
        {ordered.map((d, idx) => {
          const meta = DECISION_META[d.kind];
          const Icon = meta.icon;
          return (
            <li
              key={`${d.at}-${idx}`}
              className="flex items-start gap-3 rounded-md border border-border bg-muted/30 px-3 py-2"
            >
              <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <VBadge color={meta.color}>{meta.label}</VBadge>
                  <Caption tone="muted">
                    door {d.byName} · {d.at.slice(0, 16).replace("T", " ")}
                  </Caption>
                </div>
                {d.message ? <Body>{d.message}</Body> : null}
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
